import React, { useState, useEffect, useContext, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./EditReportModal.css";
import { UserContext } from "../../../contexts/CurrentUserContext.js";
import Select from "react-select";
import Preloader from "../../Preloader/Preloader.js";
import { getCalendar } from "../../../api/api";
import {
  getDatepickerCalendarRange,
  getCalendarRangeForPeriodMode,
} from "../../../utils/calendarUtils";
import { formatTimeWithRounding } from "../../../utils/timeUtils";
import { filterWorkTypesByFinProject } from "../../../utils/reportWorkTypesUtils";
registerLocale("ru", ru);

const checkUserAccess = (user, accessLevel) => {
  return user?.access_levels?.some((level) => level.name === accessLevel);
};

const EditReportModal = ({
  report,
  onClose,
  onSave,
  onDelete,
  userData,
  projectBlocks,
  setProjectBlocks,
  selectedUser,
  isCreateMode = false,
}) => {
  const { userData: currentUserData } = useContext(UserContext);
  const isAdmin = checkUserAccess(currentUserData, "АДМС");
  const effectiveUserData = isAdmin && selectedUser ? selectedUser : userData;

  // Инициализация состояния для доступных типов работ
  const [availableWorkTypes, setAvailableWorkTypes] = useState([]);

  // Добавляем состояния для режима периода
  const [isPeriodMode, setIsPeriodMode] = useState(false);
  const [periodStartDate, setPeriodStartDate] = useState(new Date());
  const [periodEndDate, setPeriodEndDate] = useState(new Date());
  const [periodEffort, setPeriodEffort] = useState("08:00");
  const [formStatuses, setFormStatuses] = useState({});

  // Инициализация состояния отчета
  const [editedReport, setEditedReport] = useState(() => {
    if (isCreateMode) {
      return {
        time_spent: "00:00",
        project_id: "",
        portal_job_type_id: "",
        selectedBlocks: [],
        group_id: (() => {
          // Получаем все группы из всех департаментов
          const allGroups =
            effectiveUserData.departments?.flatMap(
              (dept) => dept.groups || []
            ) || [];
          return allGroups.length === 1 ? allGroups[0].id : "";
        })(),
        date: new Date(),
        comment: "",
        user_id: effectiveUserData.id,
        is_overtime: false,
        is_remote: false,
      };
    }

    // Для существующего отчета
    if (!report) return null;

    // Форматируем дату
    let reportDate = report.fact_date_start
      ? new Date(report.fact_date_start)
      : new Date();
    if (isNaN(reportDate.getTime())) {
      reportDate = new Date(); // Если дата невалидная, используем текущую
    }

    // Нормализуем формат времени
    const normalizedTimeSpent = report.time_spent
      ? secondsToHoursMinutes(report.time_spent)
      : "00:00";

    // Убеждаемся, что время в формате HH:MM (с ведущим нулем)
    const [hours, minutes] = normalizedTimeSpent.split(":").map(Number);
    const formattedTimeSpent = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}`;

    return {
      ...report,
      time_spent: formattedTimeSpent,
      project_id: report.project?.id || "",
      portal_job_type_id: report.portal_job_type?.id || "",
      selectedBlocks: Array.isArray(report.blocks) ? report.blocks : [],
      group_id: report.group?.id || "",
      date: reportDate,
      user_id: report.user_id || effectiveUserData.id,
      is_overtime: report.is_overtime || false,
      is_remote: report.is_remote || false,
    };
  });

  // Эффект для инициализации доступных типов работ
  useEffect(() => {
    if (!isCreateMode && report?.portal_job_type) {
      setAvailableWorkTypes([report.portal_job_type]);
    }
  }, [isCreateMode, report]);

  // Эффект для обновления доступных типов работ при изменении проекта или отдела
  useEffect(() => {
    if (editedReport?.group_id && editedReport?.project_id) {
      const selectedProject = effectiveUserData.projects?.find(
        (project) => project.id === parseInt(editedReport.project_id)
      );

      if (!selectedProject) return;

      // Получаем все группы из всех департаментов
      const allGroups =
        effectiveUserData.departments?.flatMap((dept) => dept.groups || []) ||
        [];
      const allWorkTypes = allGroups
        .flatMap((group) => group.roles || [])
        .flatMap((role) => role.portal_job_type || [])
        .filter((workType) => workType && workType.id && workType.name);

      // Фильтруем только по отделу, убираем фильтрацию по типу проекта
      let filteredWorkTypes = allWorkTypes.filter((workType) => {
        const isDepartmentMatch = allGroups.some(
          (group) =>
            group.id === parseInt(editedReport.group_id) &&
            group.roles.some(
              (role) =>
                role.portal_job_type &&
                role.portal_job_type.some((type) => type.id === workType.id)
            )
        );

        return isDepartmentMatch;
      });

      filteredWorkTypes = filterWorkTypesByFinProject(
        filteredWorkTypes,
        selectedProject,
      );

      // Добавляем текущий тип работы, если он есть и не входит в отфильтрованные
      if (
        !isCreateMode &&
        report?.portal_job_type &&
        !filteredWorkTypes.find((wt) => wt.id === report.portal_job_type.id)
      ) {
        filteredWorkTypes.push(report.portal_job_type);
      }

      setAvailableWorkTypes(filteredWorkTypes);
    }
  }, [
    editedReport?.group_id,
    editedReport?.project_id,
    effectiveUserData.departments,
    effectiveUserData.projects,
    isCreateMode,
    report,
  ]);

  useEffect(() => {
    if (editedReport?.project_id) {
      const selectedProject = effectiveUserData.projects?.find(
        (p) => p.id === parseInt(editedReport.project_id)
      );
      setProjectBlocks(selectedProject?.blocks || []);
    }
  }, [editedReport?.project_id, effectiveUserData.projects, setProjectBlocks]);

  const handleChange = (field, value) => {
    if (field === "project_id") {
      const selectedProject = effectiveUserData.projects?.find(
        (p) => p.id === parseInt(value),
      );
      setProjectBlocks(selectedProject?.blocks || []);
      setEditedReport((prev) => ({
        ...prev,
        project_id: value,
        selectedBlocks: [],
        portal_job_type_id: "",
      }));
      return;
    }

    if (field === "group_id") {
      setEditedReport((prev) => ({
        ...prev,
        group_id: value,
        portal_job_type_id: "",
      }));
      return;
    }

    setEditedReport({ ...editedReport, [field]: value });
  };

  const handleBlockSelection = (blockName) => {
    setEditedReport((prev) => {
      const newSelectedBlocks = prev.selectedBlocks.includes(blockName)
        ? prev.selectedBlocks.filter((name) => name !== blockName)
        : [...prev.selectedBlocks, blockName];
      return { ...prev, selectedBlocks: newSelectedBlocks };
    });
  };

  const handleSelectAllBlocks = () => {
    setEditedReport((prev) => ({
      ...prev,
      selectedBlocks:
        prev.selectedBlocks.length === projectBlocks.length
          ? []
          : projectBlocks.map((block) => block.name),
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormValid = () => {
    if (isPeriodMode) {
      // В режиме периода проверяем только основные поля без даты и трудозатрат
      return (
        editedReport.group_id &&
        editedReport.project_id &&
        editedReport.portal_job_type_id &&
        (editedReport.selectedBlocks.length > 0 ||
          projectBlocks.length === 0) &&
        periodStartDate &&
        periodEndDate &&
        periodStartDate <= periodEndDate
      );
    } else {
      // Обычный режим - проверяем все поля
      return (
        editedReport.date &&
        editedReport.group_id &&
        editedReport.project_id &&
        editedReport.portal_job_type_id &&
        editedReport.time_spent !== "00:00" &&
        (editedReport.selectedBlocks.length > 0 || projectBlocks.length === 0)
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let selectedDate = new Date(editedReport.date);
      if (isNaN(selectedDate.getTime())) {
        selectedDate = new Date();
      }
      selectedDate.setHours(12, 0, 0, 0);
      const dateString = selectedDate.toLocaleDateString("en-CA");

      const reportData = {
        user_id: editedReport.user_id || effectiveUserData.id,
        plan_date_start: dateString,
        plan_date_end: dateString,
        fact_date_start: dateString,
        fact_date_end: dateString,
        project_id: editedReport.project_id
          ? parseInt(editedReport.project_id)
          : null,
        portal_job_type_id: editedReport.portal_job_type_id
          ? parseInt(editedReport.portal_job_type_id)
          : null,
        comment: editedReport.comment || "",
        time_spent: hoursMinutesToSeconds(editedReport.time_spent || "00:00"),
        blocks: Array.isArray(editedReport.selectedBlocks)
          ? editedReport.selectedBlocks
          : [],
        group_id: editedReport.group_id
          ? parseInt(editedReport.group_id)
          : null,
        is_overtime: editedReport.is_overtime || false,
        is_remote: editedReport.is_remote || false,
      };

      if (!isCreateMode && editedReport.id) {
        reportData.id = editedReport.id;
      }

      await onSave(reportData, isCreateMode, false); // false - не подавляем попап для обычных операций
    } catch (error) {
      console.error("Ошибка при сохранении отчета:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEffortChange = (value) => {
    // Разбиваем введенное значение на часы и минуты
    const [hours, minutes] = value.split(":").map(Number);

    // Проверяем валидность введенных значений
    if (isNaN(hours) || isNaN(minutes)) {
      return;
    }

    // Переводим в минуты для удобства расчетов
    let totalMinutes = hours * 60 + minutes;

    if (editedReport.is_overtime) {
      // Для переработки:
      // Минимальное значение - 1 час (60 минут)
      if (totalMinutes < 60) {
        totalMinutes = 60;
      }
      // Максимальное значение - 8 часов (480 минут)
      if (totalMinutes > 480) {
        totalMinutes = 480;
      }
      // Округляем до ближайших 30 минут
      totalMinutes = Math.round(totalMinutes / 30) * 30;
    } else {
      // Для обычного времени:
      // Минимальное значение - 15 минут
      if (totalMinutes < 15) {
        totalMinutes = 15;
      }
      // Максимальное значение - 8 часов (480 минут)
      if (totalMinutes > 480) {
        totalMinutes = 480;
      }
      // Округляем до ближайших 15 минут
      totalMinutes = Math.round(totalMinutes / 15) * 15;
    }

    // Преобразуем обратно в формат чч:мм
    const adjustedHours = Math.floor(totalMinutes / 60);
    const adjustedMinutes = totalMinutes % 60;

    const formattedTime = `${String(adjustedHours).padStart(2, "0")}:${String(
      adjustedMinutes
    ).padStart(2, "0")}`;

    // Обновляем состояние
    setEditedReport((prev) => ({ ...prev, time_spent: formattedTime }));
    // Принудительно перерендериваем селект
    setSelectKey((prev) => prev + 1);
  };

  function secondsToHoursMinutes(seconds) {
    return formatTimeWithRounding(seconds);
  }

  function hoursMinutesToSeconds(timeString) {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 3600 + minutes * 60;
  }

  // Получаем дату 7 дней назад
  // Для обычных пользователей — запас 4 дня включая сегодня; для АДМС — свободный выбор даты
  const fourDaysAgo = new Date();
  fourDaysAgo.setDate(fourDaysAgo.getDate() - 3);
  fourDaysAgo.setHours(0, 0, 0, 0);

  // Получаем текущую дату
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "100%",
      cursor: "pointer",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
  };

  // Создаем опции для обычного времени (15-минутные интервалы)
  const effortOptions = [];
  for (let hours = 0; hours <= 8; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      if (hours === 0 && minutes === 0) continue; // Пропускаем 00:00
      if (hours === 8 && minutes > 0) break; // Не превышаем 08:00
      const value = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}`;
      effortOptions.push({ value, label: value });
    }
  }

  // Создаем опции для переработки (30-минутные интервалы)
  const createOvertimeEffortOptions = () => {
    const options = [];
    for (let hours = 1; hours <= 8; hours++) {
      for (let minutes = 0; minutes < (hours === 8 ? 30 : 60); minutes += 30) {
        const value = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;
        options.push({ value, label: value });
      }
    }
    return options;
  };

  // Функция для получения текущих опций времени
  const getCurrentEffortOptions = () => {
    const options = editedReport.is_overtime
      ? createOvertimeEffortOptions()
      : effortOptions;

    console.log("getCurrentEffortOptions:", {
      is_overtime: editedReport.is_overtime,
      time_spent: editedReport.time_spent,
      optionsCount: options.length,
      foundOption: options.find(
        (option) => option.value === editedReport.time_spent
      ),
      firstFewOptions: options.slice(0, 5),
      allOptions: options.map((opt) => opt.value),
      lookingFor: editedReport.time_spent,
      exactMatch: options.find((opt) => opt.value === editedReport.time_spent),
      similarMatches: options.filter(
        (opt) => opt.value.includes("2:45") || opt.value.includes("02:45")
      ),
    });

    return options;
  };

  // Стили для селекта времени
  const effortSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "120px",
      marginLeft: "10px",
    }),
    control: (provided, state) => ({
      ...customStyles.control(provided, state),
      width: "120px",
      minHeight: "38px",
      height: "38px",
      display: "flex",
      alignItems: "center",
      padding: "0 8px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      margin: "0",
      padding: "0",
      display: "flex",
      alignItems: "center",
      height: "100%",
      lineHeight: "1",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      color: "#999",
      margin: "0",
      padding: "0",
      display: "flex",
      alignItems: "center",
      height: "100%",
      lineHeight: "1",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      height: "100%",
      padding: "0",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    menu: (provided, state) => ({
      ...customStyles.menu(provided, state),
      width: "120px",
      minWidth: "120px",
      maxWidth: "120px",
      position: "absolute",
      zIndex: 10001,
      overflow: "hidden",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10001,
    }),
    option: (provided, state) => ({
      ...customStyles.option(provided, state),
      padding: "8px 12px",
    }),
  };

  const handleDelete = async () => {
    if (window.confirm("Вы уверены, что хотите удалить этот отчет?")) {
      onDelete(report.id);
    }
  };

  // Добавляем состояние для данных календаря
  const [calendarData, setCalendarData] = useState({});

  // Добавляем состояние для принудительного перерендера селекта
  const [selectKey, setSelectKey] = useState(() => {
    // Если есть начальное значение time_spent, начинаем с ключа 2
    return editedReport && editedReport.time_spent ? 2 : 1;
  });

  const loadCalendarData = useCallback(async () => {
    try {
      const { dateFrom, dateTo } =
        isAdmin && isCreateMode && isPeriodMode
          ? getCalendarRangeForPeriodMode(periodStartDate, periodEndDate)
          : getDatepickerCalendarRange();

      const data = await getCalendar(dateFrom, dateTo);

      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    }
  }, [
    isAdmin,
    isCreateMode,
    isPeriodMode,
    periodStartDate,
    periodEndDate,
  ]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // Добавляем useEffect для инициализации селекта при первой загрузке
  useEffect(() => {
    console.log("useEffect инициализации селекта:", {
      editedReport: editedReport,
      time_spent: editedReport?.time_spent,
      selectKey: selectKey,
      hasTimeSpent: !!editedReport?.time_spent,
      selectKeyIsOne: selectKey === 1,
    });

    if (editedReport && editedReport.time_spent && selectKey === 1) {
      console.log(
        "Инициализация селекта с значением:",
        editedReport.time_spent
      );
      // Устанавливаем начальный ключ для селекта, чтобы он отобразил значение
      setSelectKey(2);
    }
  }, [editedReport, selectKey]);

  // Добавляем функцию для определения классов дней
  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    if (!dayData) return "";

    const classes = [];
    if (!dayData.workday_flag) classes.push("highlight-weekend");
    if (dayData.event_name) classes.push("has-event");

    return classes.join(" ");
  };

  // Функция для получения рабочих дней в периоде
  const getWorkingDaysInPeriod = (startDate, endDate) => {
    const workingDays = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);

    while (currentDate <= endDateTime) {
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      // Проверяем, что день рабочий (не выходной и не праздник)
      if (dayData?.workday_flag) {
        workingDays.push(dateString);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  // Функция для отправки отчетов за период
  const handlePeriodSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setFormStatuses({});

    try {
      const workingDays = getWorkingDaysInPeriod(
        periodStartDate,
        periodEndDate
      );

      if (workingDays.length === 0) {
        throw new Error("В выбранном периоде нет рабочих дней");
      }

      const [hours, minutes] = periodEffort.split(":").map(Number);
      const timeSpentInSeconds = hours * 3600 + minutes * 60;

      console.log(`Отправляем ${workingDays.length} отчетов за период`);

      let successCount = 0;
      let errorCount = 0;

      // Отправляем отчеты последовательно
      for (const [index, dateString] of workingDays.entries()) {
        try {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "sending",
              message: `Отправка отчета за ${dateString}...`,
            },
          }));

          const reportData = {
            user_id: editedReport.user_id || effectiveUserData.id,
            plan_date_start: dateString,
            plan_date_end: dateString,
            fact_date_start: dateString,
            fact_date_end: dateString,
            project_id: editedReport.project_id
              ? parseInt(editedReport.project_id)
              : null,
            portal_job_type_id: editedReport.portal_job_type_id
              ? parseInt(editedReport.portal_job_type_id)
              : null,
            comment: editedReport.comment || "",
            time_spent: timeSpentInSeconds,
            blocks: Array.isArray(editedReport.selectedBlocks)
              ? editedReport.selectedBlocks
              : [],
            group_id: editedReport.group_id
              ? parseInt(editedReport.group_id)
              : null,
            is_overtime: editedReport.is_overtime || false,
            is_remote: editedReport.is_remote || false,
          };

          await onSave(reportData, true, true); // true означает режим создания, второй true - подавление попапа

          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "success",
              message: `Успешно отправлено за ${dateString}`,
            },
          }));

          successCount++;
        } catch (error) {
          setFormStatuses((prev) => ({
            ...prev,
            [index]: {
              status: "error",
              message: `Ошибка за ${dateString}: ${error.message}`,
            },
          }));
          errorCount++;
        }
      }

      // Показываем итоговый результат
      if (errorCount === 0) {
        // Все отчеты отправлены успешно
        console.log(`Все ${successCount} отчетов успешно отправлены`);
        // Не закрываем модальное окно автоматически, пусть пользователь сам закроет
      } else if (successCount === 0) {
        // Все отчеты завершились ошибкой
        console.error(`Все ${errorCount} отчетов завершились ошибкой`);
        throw new Error(
          `Не удалось отправить ни одного отчета. Проверьте логи выше.`
        );
      } else {
        // Частичный успех
        console.log(
          `Отправлено ${successCount} из ${workingDays.length} отчетов. Ошибок: ${errorCount}`
        );
        throw new Error(
          `Отправлено ${successCount} из ${workingDays.length} отчетов. ${errorCount} отчетов завершились ошибкой.`
        );
      }
    } catch (error) {
      console.error("Ошибка при отправке отчетов за период:", error);
      // Показываем ошибку пользователю через попап
      setFormStatuses((prev) => ({
        ...prev,
        error: { status: "error", message: error.message },
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-content ${isSubmitting ? "submitting" : ""}`}>
        {isSubmitting && <Preloader />}
        <h2>{isCreateMode ? "Создать отчет" : "Редактировать отчет"}</h2>

        {/* Переключатель режима периода для АДМС в режиме создания */}
        {isAdmin && isCreateMode && (
          <div className="period-mode-toggle">
            <div className="toggle-container">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={isPeriodMode}
                  onChange={(e) => setIsPeriodMode(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-text">
                {isPeriodMode ? "Режим периода" : "Обычный режим"}
              </span>
            </div>
          </div>
        )}

        {/* Поля для выбора периода */}
        {isAdmin && isCreateMode && isPeriodMode && (
          <div className="period-selection">
            <h3 className="period-title">Заполнение отчета за период</h3>
            <div className="period-fields">
              <div className="period-field">
                <label>Начало периода:</label>
                <DatePicker
                  selected={periodStartDate}
                  onChange={(date) => setPeriodStartDate(date)}
                  dateFormat="dd.MM.yyyy"
                  locale="ru"
                  maxDate={currentDate}
                  dayClassName={highlightDays}
                  popperClassName="modal-datepicker-popper"
                  popperPlacement="bottom-start"
                  disabled={isSubmitting}
                />
              </div>
              <div className="period-field">
                <label>Конец периода:</label>
                <DatePicker
                  selected={periodEndDate}
                  onChange={(date) => setPeriodEndDate(date)}
                  dateFormat="dd.MM.yyyy"
                  locale="ru"
                  maxDate={currentDate}
                  minDate={periodStartDate}
                  dayClassName={highlightDays}
                  popperClassName="modal-datepicker-popper"
                  popperPlacement="bottom-start"
                  disabled={isSubmitting}
                />
              </div>
              <div className="period-field">
                <label>Часов в день:</label>
                <input
                  type="text"
                  value={periodEffort}
                  onChange={(e) => setPeriodEffort(e.target.value)}
                  placeholder="08:00"
                  disabled={isSubmitting}
                  className="effort-input"
                />
              </div>
            </div>
            <div className="period-info">
              <p>
                Рабочих дней в периоде:{" "}
                {getWorkingDaysInPeriod(periodStartDate, periodEndDate).length}
              </p>
            </div>

            {/* Отображение прогресса отправки отчетов за период */}
            {isSubmitting && Object.keys(formStatuses).length > 0 && (
              <div className="period-progress">
                <h4>Прогресс отправки отчетов:</h4>
                <div className="progress-list">
                  {Object.entries(formStatuses).map(([index, status]) => (
                    <div
                      key={index}
                      className={`progress-item ${status.status}`}
                    >
                      <span className="progress-message">{status.message}</span>
                      {status.status === "sending" && (
                        <span className="progress-spinner">⏳</span>
                      )}
                      {status.status === "success" && (
                        <span className="progress-success">✅</span>
                      )}
                      {status.status === "error" && (
                        <span className="progress-error">❌</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Кнопка закрытия после завершения отправки */}
            {!isSubmitting && Object.keys(formStatuses).length > 0 && (
              <div className="period-completion">
                <div className="completion-summary">
                  {Object.values(formStatuses).filter(
                    (s) => s.status === "success"
                  ).length > 0 && (
                    <p className="completion-success">
                      ✅ Отправка завершена! Создано{" "}
                      {
                        Object.values(formStatuses).filter(
                          (s) => s.status === "success"
                        ).length
                      }{" "}
                      отчетов.
                    </p>
                  )}
                  {Object.values(formStatuses).filter(
                    (s) => s.status === "error"
                  ).length > 0 && (
                    <p className="completion-error">
                      ❌{" "}
                      {
                        Object.values(formStatuses).filter(
                          (s) => s.status === "error"
                        ).length
                      }{" "}
                      отчетов завершились ошибкой.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="close-button"
                >
                  Закрыть
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={isPeriodMode ? handlePeriodSubmit : handleSubmit}>
          {/* Скрываем поле даты в режиме периода */}
          {!isPeriodMode && (
            <div>
              <label>Дата:</label>
              <DatePicker
                selected={editedReport.date}
                onChange={(date) => {
                  setEditedReport((prev) => ({ ...prev, date }));
                }}
                dateFormat="dd.MM.yyyy"
                locale="ru"
                minDate={isAdmin ? undefined : fourDaysAgo}
                maxDate={currentDate}
                popperClassName="modal-datepicker-popper"
                popperPlacement="bottom-start"
                dayClassName={highlightDays}
                renderDayContents={(day, date) => {
                  const dateString = date.toLocaleDateString("en-CA");
                  const dayData = calendarData[dateString];
                  return (
                    <div
                      className="day-content"
                      data-tooltip={dayData?.event_name || ""}
                    >
                      {day}
                    </div>
                  );
                }}
              />
            </div>
          )}
          <div>
            <label>Отдел:</label>
            <Select
              value={
                editedReport.group_id
                  ? {
                      value: editedReport.group_id,
                      label: (() => {
                        const allGroups =
                          effectiveUserData.departments?.flatMap(
                            (dept) => dept.groups || []
                          ) || [];
                        return allGroups.find(
                          (group) => group.id === editedReport.group_id
                        )?.name;
                      })(),
                    }
                  : null
              }
              onChange={(selectedOption) =>
                handleChange("group_id", selectedOption.value)
              }
              options={(() => {
                const allGroups =
                  effectiveUserData.departments?.flatMap(
                    (dept) => dept.groups || []
                  ) || [];
                return allGroups.map((group) => ({
                  value: group.id,
                  label: group.name,
                }));
              })()}
              styles={customStyles}
              placeholder="Выберите отдел"
              noOptionsMessage={() => "Отделы не найдены"}
            />
          </div>
          <div>
            <label>Проект:</label>
            <Select
              value={
                editedReport.project_id
                  ? {
                      value: editedReport.project_id,
                      label: effectiveUserData.projects?.find(
                        (p) => p.id === parseInt(editedReport.project_id)
                      )?.name,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                handleChange("project_id", selectedOption.value)
              }
              options={
                effectiveUserData.projects
                  ? effectiveUserData.projects
                      .filter((project) => {
                        // Исключаем проекты со статусами "Завершен", "Приостановлен", "Отказ"
                        const excludedStatuses = [
                          "Завершен",
                          "Приостановлен",
                          "Отказ",
                        ];

                        // Если у проекта нет поля status, считаем его активным
                        if (!project.status) {
                          return true;
                        }

                        return !excludedStatuses.includes(project.status);
                      })
                      .map((project) => ({
                        value: project.id.toString(),
                        label: project.name,
                      }))
                  : []
              }
              styles={customStyles}
              placeholder="Выберите проект"
              noOptionsMessage={() => "Проекты не найдены"}
            />
          </div>
          <div>
            <label>Вид работ:</label>
            <Select
              value={
                editedReport.portal_job_type_id
                  ? {
                      value: editedReport.portal_job_type_id,
                      label: availableWorkTypes.find(
                        (wt) =>
                          wt.id === parseInt(editedReport.portal_job_type_id)
                      )?.name,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                handleChange("portal_job_type_id", selectedOption.value)
              }
              options={availableWorkTypes.map((jobType) => ({
                value: jobType.id.toString(),
                label: jobType.name,
              }))}
              styles={customStyles}
              placeholder="Выберите вид работ"
              noOptionsMessage={() => "Виды работ не найдены"}
            />
          </div>
          <div>
            <label>Комментарий:</label>
            <textarea
              value={editedReport.comment}
              onChange={(e) => handleChange("comment", e.target.value)}
            />
          </div>
          {/* Скрываем поле трудозатрат в режиме периода */}
          {!isPeriodMode && (
            <div>
              <label>Трудозатраты (чч:мм):</label>
              <div className="effort-slider">
                <input
                  type="range"
                  min={editedReport.is_overtime ? "60" : "15"}
                  max="480"
                  step={editedReport.is_overtime ? "30" : "15"}
                  value={hoursMinutesToSeconds(editedReport.time_spent) / 60}
                  onChange={(e) =>
                    handleEffortChange(
                      secondsToHoursMinutes(e.target.value * 60)
                    )
                  }
                />
                <Select
                  key={`effort-select-${selectKey}`}
                  value={
                    getCurrentEffortOptions().find(
                      (option) => option.value === editedReport.time_spent
                    ) || null
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption && selectedOption.value) {
                      setEditedReport((prev) => ({
                        ...prev,
                        time_spent: selectedOption.value,
                      }));
                      // Принудительно перерендериваем селект
                      setSelectKey((prev) => prev + 1);
                    }
                  }}
                  options={getCurrentEffortOptions()}
                  styles={effortSelectStyles}
                  placeholder="Время"
                  noOptionsMessage={() => "Нет вариантов"}
                  className="effort-select"
                  isDisabled={isSubmitting}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <div className="overtime-checkbox">
                  <input
                    type="checkbox"
                    id="overtime"
                    checked={editedReport.is_overtime || false}
                    onChange={(e) => {
                      const updatedReport = {
                        ...editedReport,
                        is_overtime: e.target.checked,
                      };

                      // Получаем текущее время в минутах
                      const [hours, minutes] = editedReport.time_spent
                        .split(":")
                        .map(Number);
                      let totalMinutes = hours * 60 + minutes;

                      if (e.target.checked) {
                        // Для переработки:
                        // Минимальное значение - 1 час (60 минут)
                        if (totalMinutes < 60) {
                          totalMinutes = 60;
                        }
                        // Округляем до ближайших 30 минут
                        totalMinutes = Math.round(totalMinutes / 30) * 30;
                      } else {
                        // Для обычного времени:
                        // Минимальное значение - 15 минут
                        if (totalMinutes < 15) {
                          totalMinutes = 15;
                        }
                        // Округляем до ближайших 15 минут
                        totalMinutes = Math.round(totalMinutes / 15) * 15;
                      }

                      // Преобразуем обратно в формат чч:мм
                      const adjustedHours = Math.floor(totalMinutes / 60);
                      const adjustedMinutes = totalMinutes % 60;
                      updatedReport.time_spent = `${String(
                        adjustedHours
                      ).padStart(2, "0")}:${String(adjustedMinutes).padStart(
                        2,
                        "0"
                      )}`;

                      setEditedReport(updatedReport);
                      // Принудительно перерендериваем селект при изменении типа времени
                      setSelectKey((prev) => prev + 1);
                    }}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="overtime">Переработка</label>
                </div>
                <div className="overtime-checkbox">
                  <input
                    type="checkbox"
                    id="remote"
                    checked={editedReport.is_remote || false}
                    onChange={(e) => {
                      setEditedReport({
                        ...editedReport,
                        is_remote: e.target.checked,
                      });
                    }}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="remote">Удаленно</label>
                </div>
              </div>
            </div>
          )}
          {projectBlocks.length > 0 && (
            <div>
              <label>Блоки:</label>
              <div className="block-options">
                <button
                  type="button"
                  className={`block-option ${
                    editedReport.selectedBlocks.length === projectBlocks.length
                      ? "selected"
                      : ""
                  }`}
                  onClick={handleSelectAllBlocks}
                >
                  Выбрать все
                </button>
                {projectBlocks.map((block) => (
                  <button
                    key={block.id}
                    type="button"
                    className={`block-option ${
                      editedReport.selectedBlocks.includes(block.name)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleBlockSelection(block.name)}
                  >
                    {block.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="modal-buttons">
            <div className="modal-buttons-main">
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                className={!isFormValid() ? "button-disabled" : ""}
              >
                {isSubmitting
                  ? "Сохранение..."
                  : isPeriodMode
                  ? "Отправить за период"
                  : isCreateMode
                  ? "Создать"
                  : "Сохранить"}
              </button>
              <button type="button" onClick={onClose} disabled={isSubmitting}>
                Отмена
              </button>
            </div>
            {!isCreateMode && (
              <button
                type="button"
                onClick={handleDelete}
                className="delete-button"
                disabled={isSubmitting}
              >
                Удалить отчет
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReportModal;
